import CustomImageComponent from '@/components/shared/custom/custom-image';
import { Box, Typography } from '@mui/material';
import { memo, useMemo, useRef } from 'react';
import styles from './Lineup.module.css';
import Porcentajes from '../complementos/Porcentajes';
import LineupPlayerAlternatives from './LineupPlayerAlternatives';
import Apercibidos from '../complementos/Apercibidos';
import TshirtIconComponent from '@/components/page-components/fixtures/fixture-player-stadium-player/tshirt-icon-component';
import PuntuacionConfirmada from '../complementos/PuntuacionConfirmada';
import JugadorEventCard from '../Jugadores/JugadorEventCard';
import { getTopForPlayer } from '@/helpers/analisis-plantilla-fantasy-helper';
import useFormationName from '../hooks/useFormationName';
import LineupFantasySelector from './LineupFantasySelector';
import useStatusIcon from '@/DesignSystem/hooks/useStatusIcon';
import useFantasySelected from '@/DesignSystem/hooks/useFantasySelected';
import { FANTASY } from '@/helpers/biwenger-helper';
import JugadorAlineacionMercadoFantasy from '../complementos/JugadorAlineacionMercadoFantasy';
import { toPriceInEuros } from '@/helpers/prices-helper';
import useIconoEstadoDelTiempo from '../hooks/useIconoEstadoDelTiempo';
import NoInscritoIcon from '@/components/shared/icons/recommendation-icons/no-inscrito-icon';
import LineupFantasySelectorConfirmed from './LineupFantasySelectorConfirmed';
import useShowPlayerAlineacionProbable from '../hooks/useShowPlayerAlineacionProbable';
import CountryConvocado from '../complementos/CountryConvocado';
import TeamStatsFloatingMenu from './TeamStatsFloatingMenu';
import RecomendacionesIcon from '@/components/recomendaciones/RecomendacionesIcon';
import { useAlineacionesContext } from '../hooks/useAlineacionesContext';
import PredictingPromotionIcon from './PredictingPromotionIcon';
import LineupJarvisComponent from './LineupJarvisComponent';
import LottieIconComponent, { LOTTIE_ICONS } from '@/DesignSystem/features/lottie/LottieIconComponent';
import { usePlayerRankings } from '@/hooks/usePlayerRankings';
import PlayerCardWrapper from './PlayerCardWrapper';

const LineupVerticalPlayerCard = memo(
    ({ player, players, isUnderDesktop, mode, estadisticasPartido, events, top, showPercentage = true }) => {
        const { fantasy } = useFantasySelected();
        const size = isUnderDesktop ? 70 : 80;
        const handleShowPlayerAlineacionProbable = useShowPlayerAlineacionProbable(player);

        // Verificar si el jugador tiene rankings globales (retorna: { tier: "gold"|"silver"|"bronze", category: "mercado"|"fantasy"|"mister"|"marca"|"biwenger" } o null)
        const playerRanking = usePlayerRankings(player.id);

        const estadisticaPartido = estadisticasPartido?.estadisticas?.[player?.id];
        const playerEvents = useMemo(() => {
            return events?.filter(
                (event) =>
                    (event.player.id === player.id && event.player.id) ||
                    (event.assist.id === player.id && event.assist.id)
            );
        }, [events, player.id]);

        const statusIcon = useStatusIcon({
            status: player?.status,
            statusLevel: player?.statusLevel,
            width: 14,
            height: 14,
            skipIfNotOk: true,
        });

        const getPredictData = () => {
            if (mode !== 'predict') return null;

            if (
                fantasy === FANTASY.FANTASY ||
                fantasy === FANTASY.FANTASY_MARCA ||
                fantasy === FANTASY.BIWENGER ||
                fantasy === FANTASY.MISTER_FANTASY
            ) {
                let subida = player.laLigaFantasySubida;
                let marketValue = player.laLigaFantasyMarketValue;

                if (fantasy === FANTASY.FANTASY_MARCA) {
                    subida = player.fantasyMarcaSubida;
                    marketValue = player.fantasyMarcaMarketValue;
                } else if (fantasy === FANTASY.MISTER_FANTASY) {
                    subida = player.misterFantasySubida;
                    marketValue = player.misterFantasyMarketValue;
                } else if (fantasy === FANTASY.BIWENGER) {
                    subida = player.biwengerSubida;
                    marketValue = player.biwengerMarketValue;
                }

                return (
                    <>
                        <JugadorAlineacionMercadoFantasy
                            value={subida}
                            width="100%"
                            height="15px"
                            margin={'5px auto 0 auto'}
                            fantasy={fantasy}
                        />
                        <Typography
                            fontSize={10}
                            color={'#ccc'}
                            margin={'2px auto 0 auto'}
                            width={'100%'}
                            textAlign={'center'}
                        >
                            {toPriceInEuros(marketValue)}
                        </Typography>
                    </>
                );
            }
        };

        return (
            <PlayerCardWrapper
                className={styles.estadioLinePlayer}
                playerRanking={playerRanking}
                size={size}
                top={top}
                onClick={handleShowPlayerAlineacionProbable}
            >
                <Box position={'relative'}>
                    {player.image ? (
                        <Box position={'relative'} width={size} height={size}>
                            <CustomImageComponent
                                src={player.image}
                                alt={player.name}
                                width={size}
                                height={size}
                            />

                            {playerEvents?.length > 0 && (
                                <Box
                                    position={'absolute'}
                                    top={-10}
                                    left={0}
                                    width={'100%'}
                                    display={'flex'}
                                    alignItems={'center'}
                                >
                                    {playerEvents.map((event) => (
                                        <JugadorEventCard
                                            key={event.id}
                                            event={event}
                                            player={player}
                                            direction={'row'}
                                            includeTime={true}
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box sx={{ position: 'absolute', bottom: -2, left: 0 }}>{statusIcon}</Box>

                            <CountryConvocado
                                country={player.countryConvocado}
                                isConvocado={player.isConvocado}
                                position={'absolute'}
                                top={5}
                                left={5}
                            />

                            {player.isNoInscrito && (
                                <Box sx={{ position: 'absolute', top: 5, left: 5 }}>
                                    <NoInscritoIcon width={14} height={14} />
                                </Box>
                            )}

                            {player.isBufon && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '3px',
                                        right: '22px',
                                    }}
                                >
                                    <LottieIconComponent icon={LOTTIE_ICONS.CLOWN} width={30} height={30} />
                                </Box>
                            )}

                            <Apercibidos
                                cards={player.cards}
                                size={12}
                                color={'#fddf00'}
                                position={'absolute'}
                                bottom={0}
                                left={'0px'}
                            />

                            {mode === 'predict' && showPercentage && (
                                <Porcentajes
                                    porcentaje={player.chance}
                                    fontSize={10}
                                    width={'30px'}
                                    height={'15px'}
                                    position={'absolute'}
                                    bottom={0}
                                    right={0}
                                    comentario={player.comentario}
                                    leftComentario={30}
                                />
                            )}
                        </Box>
                    ) : (
                        <TshirtIconComponent
                            primary={player.primary}
                            colorNumber={player.colorNumber}
                            number={player.number}
                            width={size * 0.8}
                            height={size * 0.8}
                        />
                    )}

                    {getPredictData()}

                    {(mode === 'confirmed' || mode === 'last') && (
                        <PuntuacionConfirmada
                            estadisticaPartido={estadisticaPartido}
                            width="30px"
                            margin="5px auto 0 auto"
                            position="absolute"
                            bottom={0}
                            right={0}
                        />
                    )}
                </Box>
                <Typography
                    variant="body1"
                    color={'#fff'}
                    noWrap
                    maxWidth={'90%'}
                    className={playerRanking ? 'orbitron-font' : ''}
                    sx={playerRanking ? { fontSize: 10, marginTop: 0.5 } : { fontSize: 12 }}
                >
                    {player.name}
                </Typography>

                <LineupPlayerAlternatives player={player} players={players} fontSize={14} />
            </PlayerCardWrapper>
        );
    }
);

LineupVerticalPlayerCard.displayName = 'LineupVerticalPlayerCard';

function LineupVertical({
    lineup,
    prediccionTiempo,
    mode,
    estadisticasPartido,
    events,
    isAnalitica,
    formation,
    isUnderDesktop,
    teamId,
    fantasyEnabled,
    weatherIconPosition = 'right', // ✅ Nueva prop: 'left' o 'right'
    ...props
}) {
    const containerRef = useRef(null);
    const isPredict = mode === 'predict';

    // Obtener el editor seleccionado del contexto (funciona para Equipo y Partido)
    const { selectedEditorLineup } = useAlineacionesContext() || {};

    // Fusionar lineup con datos del editor si está seleccionado
    const finalLineup = useMemo(() => {
        // Si no hay editor seleccionado o no es el equipo correcto, usar lineup original
        if (!selectedEditorLineup || selectedEditorLineup.team !== teamId) {
            return lineup;
        }

        // Crear un mapa de players del editor por ID
        const editorPlayersMap = new Map(selectedEditorLineup.players?.map((p) => [p.id, p]) || []);

        // Fusionar: mantener todos los datos del jugador original pero usar grid y chance del editor
        return (
            lineup?.map((player) => {
                const editorPlayer = editorPlayersMap.get(player.id);

                if (editorPlayer) {
                    return {
                        ...player,
                        grid: editorPlayer.grid,
                        chance: editorPlayer.chance,
                        alternatives: editorPlayer.alternatives,
                    };
                }

                return player;
            }) || []
        );
    }, [lineup, selectedEditorLineup, teamId]);

    // Determinar si mostrar porcentajes
    const showPercentage = useMemo(() => {
        // Si hay editor seleccionado, usar su configuración usePercentage
        if (selectedEditorLineup && selectedEditorLineup.team === teamId) {
            return selectedEditorLineup.usePercentage;
        }
        // Si no hay editor seleccionado, mostrar porcentajes por defecto en modo predict
        return true;
    }, [selectedEditorLineup, teamId]);

    const formationClean = useMemo(() => {
        // Si hay editor seleccionado, usar su formación
        if (selectedEditorLineup && selectedEditorLineup.team === teamId && selectedEditorLineup.formation) {
            return selectedEditorLineup.formation.replace('alineacion_', '');
        }

        if (!formation) return null;
        return formation.replace('alineacion_', '');
    }, [formation, selectedEditorLineup, teamId]);

    const lines = useMemo(() => {
        if (!formationClean || !finalLineup?.length) return [];

        const parts = formationClean.split('-').map(Number); // [4, 2, 3, 1]
        const playersExcludingGoalkeeper = finalLineup
            .filter((player) => player.grid !== 11 && player.grid > 0)
            .sort((a, b) => a.grid - b.grid);

        let currentIndex = 0;
        const result = parts.map((count) => {
            const group = playersExcludingGoalkeeper.slice(currentIndex, currentIndex + count);
            currentIndex += count;
            return group;
        });

        return result;
    }, [formationClean, finalLineup]);

    const portero = useMemo(() => {
        return finalLineup?.find((player) => player.grid === 11);
    }, [finalLineup]);

    const renderLine = (players, gap) => {
        if (!players?.length) return null;
        return (
            <Box className={styles.estadioLine} sx={{ gap: gap }}>
                {players
                    ?.sort((a, b) => a.grid - b.grid)
                    ?.map((player, index) => {
                        const top = getTopForPlayer(formation, player.grid);

                        return (
                            <LineupVerticalPlayerCard
                                player={player}
                                key={`${player.id}-${index}`}
                                players={finalLineup}
                                isUnderDesktop={isUnderDesktop}
                                mode={mode}
                                estadisticasPartido={estadisticasPartido}
                                events={events}
                                isAnalitica={isAnalitica}
                                top={top}
                                showPercentage={showPercentage}
                            />
                        );
                    })}
            </Box>
        );
    };

    const getGap = (linea, cantidad) => {
        if (linea === 0) {
            switch (cantidad) {
                case 3:
                    return '10%';
                case 4:
                    return '8%';
                case 5:
                    return '2%';
                case 6:
                    return '0%';
                default:
                    return 0;
            }
        } else if (linea === 1) {
            switch (cantidad) {
                case 2:
                    return '10%';
                case 3:
                    return '10%';
                case 4:
                    return '8%';
                case 5:
                    return '2%';
                default:
                    return 0;
            }
        } else if (linea === 2) {
            switch (cantidad) {
                case 2:
                    return '8%';
                default:
                    return '6%';
            }
        } else if (linea === 3) {
            return '6%';
        }

        return 0;
    };

    const tiempo = useIconoEstadoDelTiempo(prediccionTiempo, weatherIconPosition);

    if (!formationClean && !lines?.length) {
        return (
            <Box
                className={styles.estadio}
                sx={{
                    width: '100%',
                    height: isUnderDesktop ? '600px' : '650px',
                    transition: 'height 0.2s ease',
                    position: 'relative',
                    backgroundImage:
                        'url(https://assets.analiticafantasy.com/estadio/estadio-confirmada.jpg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                ref={containerRef}
                {...props}
            >
                <Typography
                    sx={{
                        fontSize: 16,
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 500,
                        textAlign: 'center',
                        letterSpacing: '0.5px',
                        width: '50%',
                    }}
                >
                    No tenemos alineación disponible para el equipo en este partido.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            className={styles.estadio}
            sx={{
                width: '100%',
                height: isUnderDesktop ? '600px' : '650px',
                transition: 'height 0.2s ease',
                position: 'relative',
                backgroundImage:
                    mode === 'confirmed' || mode === 'last'
                        ? 'url(https://assets.analiticafantasy.com/estadio/estadio-confirmada.jpg)'
                        : 'url(https://assets.analiticafantasy.com/estadio/estadio.jpg)',
            }}
            ref={containerRef}
            {...props}
        >
            {formation && (
                <Typography
                    color="rgba(255, 255, 255, 0.5)"
                    sx={{
                        position: 'absolute',
                        bottom: 5,
                        right: 8,
                        fontSize: 10,
                    }}
                >
                    Formación: {useFormationName(formation)}
                </Typography>
            )}
            {portero && renderLine([portero], 0)}
            {lines.map((linePlayers, idx) => {
                return <Box key={idx}>{renderLine(linePlayers, getGap(idx, linePlayers.length))}</Box>;
            })}
            {tiempo}
            {isPredict ? (
                <Box sx={{ position: 'absolute', bottom: 10, left: 10 }}>
                    <LineupFantasySelector />
                </Box>
            ) : (
                <Box sx={{ position: 'absolute', bottom: 10, left: 10 }}>
                    <LineupFantasySelectorConfirmed fantasyEnabled={fantasyEnabled} />
                </Box>
            )}
            <TeamStatsFloatingMenu teamId={teamId} top="10px" left="10px" />
            {isPredict && (
                <>
                    <RecomendacionesIcon sx={{ position: 'absolute', bottom: '80px', right: '15px' }} />
                    <PredictingPromotionIcon />
                    <LineupJarvisComponent teamId={teamId} />
                </>
            )}
        </Box>
    );
}

LineupVertical.displayName = 'LineupVertical';

export default memo(LineupVertical);
